import { DependencyLifeTime, Injectable } from "@miracledevs/paradigm-web-di";
import { ChartData, ChartOptions } from "chart.js";
import { createElement, ReactElement } from "react";
import { Line } from "react-chartjs-2";
import { ContextCollection } from "../../contexts/DependencyContext";

export type ChartSize = 'small' | 'medium' | 'large';
export type ZoomMode = 'xy' | 'x' | 'y';

@Injectable({ collection: ContextCollection, lifeTime: DependencyLifeTime.Singleton })
export class ChartService
{
    zoomMode: ZoomMode = "xy";
    defaultOptions: ChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: {
                display: false,
                text: "Data chart"
            },
            tooltip: {
                enabled: true,
                mode: "index",
                intersect: false,
                callbacks: {
                    title: () => '',
                    label: () => ''
                }
            },
            legend: {
                display: false
            }
        },
        scales: {
            x: {
                type: 'timeseries',
                position: "bottom",
                display: true,
                title: {
                    display: true,
                    text: "Time"
                }
            },
            y: {
                display: true,
                beginAtZero: false,
                ticks: {
                    maxTicksLimit: 5,
                    callback: function (value: any)
                    {
                        if (typeof value === "number")
                        {
                            const rawValLength = value.toString();
                            const fixedValLength = value.toFixed(5).toString();
                            return fixedValLength.length < rawValLength.length ? fixedValLength : rawValLength;
                        }
                    }
                },
                title: {
                    display: true,
                    text: "Value"
                }
            }
        }
    };

    setZoom(zoomMode: ZoomMode): void {
        this.zoomMode = zoomMode;
    }

    getZoom(): ZoomMode {
        return this.zoomMode;
    }

    handleKeyDown (event: KeyboardEvent): void {
        if (event.repeat) return;

        if ((event.code === "ControlLeft" || event.code === "AltLeft") && this.getZoom() === "xy") {
            this.setZoom(event.code === "ControlLeft" ? "x" : "y");
        }
    }

    handleKeyUp(event: KeyboardEvent): void {
        if (this.getZoom() !== "xy") {
            this.setZoom("xy");
            if (event.target) {
                this.cleanListeners();
                this.setEventListeners();
                const eventTarget = event.target as HTMLElement;
                eventTarget.click();
                eventTarget.focus();
            }
        };
    }

    setEventListeners(elem?: HTMLElement): void {
        const target = elem || window;
        target.addEventListener("keydown", e => this.handleKeyDown(e as KeyboardEvent), { once: true });
        target.addEventListener("keyup", e => this.handleKeyUp(e as KeyboardEvent), { once: true });
    }

    cleanListeners(elem?: HTMLElement): void {
        const target = elem || window;
        target.removeEventListener("keydown", e => this.handleKeyDown(e as KeyboardEvent));
        target.removeEventListener("keyup", e => this.handleKeyUp(e as KeyboardEvent));
    }

    setChart(series: ChartData, options: ChartOptions, size: number): ReactElement {
        return createElement(Line, {
            data: series as ChartData<"line">,
            options: options as ChartOptions<"line">,
            height: size
        })
    }
}